/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'clsx';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Cancel as CancelIcon, Delete as DeleteIcon, RestorePage as ReimportIcon } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';

import { HUChip } from '../../../../../../../../elements/HUChip/HUChip';
import { Namespaces } from '../../../../../../../../i18n';
import { useTableStyles } from '../../../../../../../../styles/table';
import { DatasetImport } from '../../../../../../../../types/dataset/DatasetImport';
import { DatasetImportStatus } from '../../../../../../../../types/dataset/DatasetImportStatus';
import { getDSImportStatusColor } from '../../../../../../../../utils/getDSImportStatusColor';
import { DatasetImportsTableContext } from '../../DatasetImportsTable.context';
import { useDatasetImportsTableImportRowStyles } from './DatasetImportsTableImportRow.styles';

type DatasetImportsTableRowProps = {
  datasetImport: DatasetImport;
  databaseName: string;
};

export function DatasetImportsTableImportRow(props: DatasetImportsTableRowProps) {
  const tableClasses = useTableStyles();
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = useDatasetImportsTableImportRowStyles();

  const { datasetImport, databaseName } = props;

  const { operations, isReadonly, dataset, status, allowReimport, isConflictingOid, isPresent } = datasetImport;

  const {
    toggleSchemaCollapsed,
    isCollapsedSchema,
    handleCancelDatasetData,
    handleReimportDatasetData,
    handleSetNotReadonlyDatasetData,
    handleDeleteDatasetData,
  } = useContext(DatasetImportsTableContext);

  const collapsed = useMemo<boolean>(
    () => isCollapsedSchema(dataset.schema, databaseName),
    [databaseName, dataset.schema, isCollapsedSchema],
  );

  const { reimportBtnTooltip, isDisabledReimport } = useMemo<{
    isDisabledReimport: boolean;
    reimportBtnTooltip: string;
  }>(() => {
    if (!allowReimport) {
      if (isConflictingOid) {
        return {
          isDisabledReimport: true,
          reimportBtnTooltip: t('details.datasets.dataset.action_disable_reason.schema_conflict')!,
        };
      } else if (!isReadonly && isPresent) {
        return {
          isDisabledReimport: true,
          reimportBtnTooltip: t('details.datasets.dataset.action_disable_reason.reimport_drop_schema_first')!,
        };
      }
    }

    return {
      isDisabledReimport: false,
      reimportBtnTooltip: t('details.datasets.dataset.reimport')!,
    };
  }, [allowReimport, isConflictingOid, isPresent, isReadonly, t]);

  const { deleteBtnTooltip, isDisabledDelete } = useMemo<{
    isDisabledDelete: boolean;
    deleteBtnTooltip: string;
  }>(() => {
    if (!allowReimport) {
      if (!isReadonly && isPresent) {
        return {
          isDisabledDelete: true,
          deleteBtnTooltip: t('details.datasets.dataset.action_disable_reason.delete_drop_schema_first')!,
        };
      }
    }
    return {
      isDisabledDelete: false,
      deleteBtnTooltip: t('details.datasets.dataset.delete')!,
    };
  }, [allowReimport, isPresent, isReadonly, t]);

  return (
    <TableRow className={classes.mainRow}>
      <TableCell className={tableClasses.bodyTableCell}>
        {operations.length > 0 && (
          <IconButton
            className={classes.collapseIconButton}
            onClick={() => toggleSchemaCollapsed(dataset.schema, databaseName)}
            size="large"
          >
            {!collapsed ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell} />
      <TableCell
        classes={{
          root: cx(tableClasses.bodyTableCell, classes.nameCell),
        }}
      >
        <Typography component="p" className={tableClasses.bodyTableCellText}>
          {dataset.name}
        </Typography>
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <Typography component="p" className={tableClasses.bodyTableCellText}>
          {dataset.schema}
        </Typography>
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <Typography component="p" className={tableClasses.bodyTableCellText}>
          {dataset.category.i18n.title}
        </Typography>
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <Typography component="p" className={tableClasses.bodyTableCellText}>
          {isReadonly
            ? t('details.datasets.dataset.import_type.readonly')
            : t('details.datasets.dataset.import_type.not_readonly')}
        </Typography>
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <HUChip
          size="small"
          {...(status === DatasetImportStatus.Idle
            ? isPresent
              ? {
                  label: t('details.datasets.dataset.import_status.idle_present'),
                  color: '#35B44E',
                }
              : {
                  label: t('details.datasets.dataset.import_status.idle_not_present'),
                  color: '#F1302F',
                }
            : {
                color: getDSImportStatusColor(status),
                label: t(`details.datasets.dataset.import_status.${status}`),
              })}
        />
      </TableCell>
      <TableCell className={tableClasses.bodyTableCell}>
        <div className={classes.datasetControls}>
          {(status === DatasetImportStatus.Importing || status === DatasetImportStatus.Pending) && (
            <Tooltip title={t('details.datasets.dataset.cancel')!}>
              <IconButton
                disabled={false}
                onClick={() =>
                  handleCancelDatasetData({
                    wantToCancel: datasetImport,
                    isModalOpened: true,
                  })
                }
                size="small"
                classes={{ root: classes.controlIcon }}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          )}

          {status === DatasetImportStatus.Idle && (
            <Tooltip title={reimportBtnTooltip}>
              <span>
                <IconButton
                  disabled={isDisabledReimport}
                  size="small"
                  classes={{
                    root: cx(classes.controlIcon, {
                      [classes.disabledErrorIcon]: isDisabledReimport,
                    }),
                  }}
                  onClick={() =>
                    handleReimportDatasetData({
                      wantToReimport: datasetImport,
                      isModalOpened: true,
                    })
                  }
                >
                  <ReimportIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {status === DatasetImportStatus.Idle && (
            <Tooltip title={deleteBtnTooltip}>
              <span>
                <IconButton
                  disabled={isDisabledDelete}
                  size="small"
                  classes={{
                    root: cx(classes.controlIcon, {
                      [classes.disabledErrorIcon]: isDisabledDelete,
                    }),
                  }}
                  onClick={() =>
                    handleDeleteDatasetData({
                      datasetImport: datasetImport,
                      isModalOpened: true,
                    })
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {isReadonly
            ? status === DatasetImportStatus.Idle &&
              isPresent && (
                <Tooltip title={t('details.datasets.dataset.set_not_readonly')!}>
                  <IconButton
                    disabled={false}
                    size="small"
                    classes={{ root: classes.controlIcon }}
                    onClick={() =>
                      handleSetNotReadonlyDatasetData({
                        datasetImport: datasetImport,
                        isModalOpened: true,
                      })
                    }
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )
            : null}
        </div>
      </TableCell>
    </TableRow>
  );
}
